import React from 'react';

// The types here should match our extracted JSON structure
interface ContentNode {
  type: string;
  html?: string;
  text?: string;
  items?: any[];
  cards?: any[];
  variant?: string;
  people?: any[];
  values?: string[];
  headers?: string[];
  rows?: string[][];
  subs?: any[];
  benefits?: any[];
  role?: string;
  name?: string;
  email?: string;
  tag?: string;
  num?: string;
  grade?: string;
  mode?: string;
  rate?: string;
  small?: string;
}

interface Subsection {
  title: string;
  content: ContentNode[];
}

interface Section {
  id: string;
  num: string;
  title: string;
  subsections: Subsection[];
}

interface ContentProps {
  theme: 'cnw' | 'krissco';
  sections: Section[];
}

const HandbookContent: React.FC<ContentProps> = ({ theme, sections }) => {
  const renderIcon = (iconName: string, className: string = "card-icon") => {
    const iconMap: Record<string, string> = {
      'track_changes': 'track_changes',
      'bar_chart': 'bar_chart',
      'warning': 'warning',
      'payments': 'payments',
      'call': 'call',
      'chat': 'chat',
      'campaign': 'campaign',
      'person': 'person',
      'school': 'school',
      'work': 'work',
      'task': 'task',
      'calendar_month': 'calendar_month',
      'support_agent': 'support_agent',
      'group': 'group'
    };

    if (iconMap[iconName]) {
      return <span className={`material-symbols-outlined ${className}`}>{iconName}</span>;
    }
    return <div className={className}>{iconName}</div>;
  };

  const processHtmlWithIcons = (html: string) => {
    if (!html) return '';

    // Replace icon names at the start of the string or preceded by whitespace
    const iconNames = ['warning', 'chat', 'call', 'campaign'];
    let processed = html;

    iconNames.forEach(name => {
      // Use regex to find the name if it's at the start or follows whitespace/br
      const regex = new RegExp(`(^|\\s|>)(${name})(\\s|:|<strong>)`, 'g');
      processed = processed.replace(regex, (match, prefix, icon, suffix) => {
        return `${prefix}<span class="material-symbols-outlined icon-inline">${icon}</span>${suffix}`;
      });
    });

    // Detect emails and wrap in mailto:
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    processed = processed.replace(emailRegex, '<a href="mailto:$1" class="handbook-link">$1</a>');

    // Detect 10-digit phone numbers and wrap in tel:
    const phoneRegex = /(\d{10})/g;
    processed = processed.replace(phoneRegex, '<a href="tel:$1" class="handbook-link">$1</a>');

    return processed;
  };

  return (
    <div className="handbook-content">
      {sections.map((section) => (
        <div key={section.id} className="handbook-section" id={section.id}>
          <div className="section-header">
            <div className="section-num">{section.num}</div>
            <h2 className="section-title">{section.title}</h2>
          </div>

          {section.subsections.map((sub, idx) => (
            <div key={idx} className="handbook-subsection">
              <h3 className="subsection-title">{sub.title}</h3>

              {sub.content.map((node, cIdx) => {
                switch (node.type) {
                  case 'p':
                    return <p key={cIdx} dangerouslySetInnerHTML={{ __html: processHtmlWithIcons(node.html || '') }} />;
                  case 'h4':
                    return <h4 key={cIdx}>{node.text}</h4>;
                  case 'ul':
                    return (
                      <ul key={cIdx}>
                        {node.items?.map((li, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: processHtmlWithIcons(li) }} />
                        ))}
                      </ul>
                    );
                  case 'ol':
                    return (
                      <ol key={cIdx}>
                        {node.items?.map((li, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: processHtmlWithIcons(li) }} />
                        ))}
                      </ol>
                    );
                  case 'card-grid':
                    return (
                      <div key={cIdx} className="card-grid">
                        {node.cards?.map((card, i) => (
                          <div key={i} className="card">
                            {renderIcon(card.icon)}
                            <div className="card-title">{card.title}</div>
                            <div className="card-desc">{card.desc}</div>
                          </div>
                        ))}
                      </div>
                    );
                  case 'info-box':
                    return (
                      <div key={cIdx} className={`info-box ${node.variant}`}>
                        <div dangerouslySetInnerHTML={{ __html: processHtmlWithIcons(node.html || '') }} />
                      </div>
                    );
                  case 'people-grid':
                    return (
                      <div key={cIdx} className="people-grid">
                        {node.people?.map((person, i) => (
                          <div key={i} className="person-card">
                            <div className="person-avatar">{person.avatar}</div>
                            <div className="person-name">{person.name}</div>
                            <div className="person-role">{person.role}</div>
                            <div className="person-bio">{person.bio}</div>
                          </div>
                        ))}
                      </div>
                    );
                  case 'values-grid':
                    return (
                      <div key={cIdx} className="values-grid">
                        {node.values?.map((val, i) => (
                          <div key={i} className="value-pill">
                            <div className="value-dot" />
                            {val}
                          </div>
                        ))}
                      </div>
                    );
                  case 'table':
                    return (
                      <div key={cIdx} className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              {node.headers?.map((th, i) => <th key={i}>{th}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {node.rows?.map((row, i) => (
                              <tr key={i}>
                                {row.map((td, j) => (
                                  <td key={j} dangerouslySetInnerHTML={{ __html: processHtmlWithIcons(td) }} />
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  case 'sub-cards':
                    return (
                      <div key={cIdx} className="sub-cards">
                        {node.subs?.map((sub, i) => (
                          <div key={i} className="sub-card">
                            <div className="sub-card-name">{sub.name}</div>
                            <div className="sub-card-desc">{sub.desc}</div>
                          </div>
                        ))}
                      </div>
                    );
                  case 'dress-grid':
                    return (
                      <div key={cIdx} className="dress-grid">
                        {node.cards?.map((card, i) => (
                          <div key={i} className={`dress-card ${card.type === 'deny' ? 'deny' : ''}`}>
                            <div className="dress-card-title">
                              <div className="dot" /> {card.title}
                            </div>
                            <ul>
                              {card.items?.map((item: string, j: number) => (
                                <li key={j}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    );
                  case 'grievance-grid':
                    return (
                      <div key={cIdx} className="grievance-grid">
                        {node.items?.map((item, i) => (
                          <div key={i} className="grievance-item">
                            {item.text}
                            <small>{item.small}</small>
                          </div>
                        ))}
                      </div>
                    );
                  case 'benefits-row':
                    return (
                      <div key={cIdx} className="benefits-row">
                        {node.benefits?.map((b, i) => (
                          <div key={i} className="benefit-item">
                            {b.icon ? renderIcon(b.icon, "big") : renderIcon(b.big, "big")}
                            {b.icon && b.big && <div className="big-text">{b.big}</div>}
                            <div className="label">{b.label}</div>
                          </div>
                        ))}
                      </div>
                    );
                  case 'icc-card':
                    return (
                      <div key={cIdx} className="icc-card">
                        <div>
                          <div className="icc-role">{node.role}</div>
                          <div className="icc-name">{node.name}</div>
                          <div className="icc-email">
                            <a href={`mailto:${node.email}`} className="handbook-link">{node.email}</a>
                          </div>
                        </div>
                        <div className={`icc-tag ${node.tag?.toLowerCase().includes('external') ? 'external' : ''}`}>
                          {node.tag}
                        </div>
                      </div>
                    );
                  case 'tier-row':
                    return (
                      <div key={cIdx} className="tier-row">
                        <div className="tier-num">{node.num}</div>
                        <div className="tier-info">
                          <div className="tier-grade">{node.grade}</div>
                          <div className="tier-mode">{node.mode}</div>
                        </div>
                        <div className="tier-rate">{node.rate}</div>
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          ))}

          <hr />
        </div>
      ))}
    </div>
  );
};

export default HandbookContent;
